import React, { ReactNode } from "react";

import Box from "veneer/Box";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography, { TypographyProps } from "veneer/Typography";

export interface PanelProps {
  bgcolor?: (typeof Colors)[keyof typeof Colors];
  borderRadius?: number;
  children?: ReactNode;
  divider?: boolean;
  gap?: number;
  noHeader?: boolean;
  mb?: number;
  p?: number;
  py?: number;
  px?: number;
  role?: string;
  spacing?: number;
  subtitle?: ReactNode;
  tabIndex?: number;
  title?: ReactNode;
  titleProps?: TypographyProps;
  toolbox?: ReactNode;
  width?: number | string;
}

const defaultTitleProps: TypographyProps = {
  color: Colors.gray1000,
  variant: "h5",
};

const ShadowBox = styled(Box)({
  boxShadow: "0 1px 1px rgba(0, 0, 0, 0.1)",
});

const Panel = ({
  bgcolor = Colors.white,
  borderRadius = 2,
  children,
  divider = false,
  gap,
  mb = 0.5,
  noHeader = false,
  p = 2,
  py,
  px,
  spacing,
  subtitle,
  title,
  titleProps = defaultTitleProps,
  toolbox,
  width,
}: PanelProps) => {
  const showHeader = !noHeader && (title || subtitle || toolbox);
  const dividerProps = divider && {
    borderTop: 2,
    borderColor: "divider",
  };
  const BoxComponent = divider ? ShadowBox : Box;

  return (
    <BoxComponent
      bgcolor={bgcolor}
      borderRadius={borderRadius}
      dataTestId="panel"
      mb={mb}
      p={p}
      position="relative"
      px={px}
      py={py}
      width="100%"
      {...dividerProps}
    >
      {showHeader && (
        <Stack
          alignItems="center"
          dataTestId="panel-header"
          direction="row"
          justifyContent="space-between"
          mb={2}
        >
          <Stack>
            {title && <Typography {...titleProps}>{title}</Typography>}
            {subtitle && <Typography variant="caption">{subtitle}</Typography>}
          </Stack>

          {toolbox && <>{toolbox}</>}
        </Stack>
      )}
      <Stack
        dataTestId="panel-content"
        gap={gap}
        height="100%"
        spacing={spacing}
        width={width}
      >
        {children}
      </Stack>
    </BoxComponent>
  );
};

export default Panel;
export { Panel };
